// NewsletterCTA — chamada discreta para inscrição por e-mail.
// Consome /api/integrations/newsletter/subscribe (proxy gracioso para a landing).
// App NÃO persiste e NÃO envia e-mail; toda a entrega fica na landing oficial.
//
// Props:
//   variant: "inline" (input + botão) | "compact" (botão único com modal/expand)
//   interest: "daily" | "plans" | "prayer-week" | "all"
//   title: string opcional (default por interest)
//   subtitle: string opcional
//   testidPrefix: prefixo para data-testid

import React, { useState } from "react";
import { Mail, Send, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const DEFAULT_TITLES = {
  daily: "Receba o devocional por e-mail",
  plans: "Receba os planos por e-mail",
  "prayer-week": "Participar da Semana de Oração",
  all: "Receber mensagens diárias",
};

const DEFAULT_SUBTITLES = {
  daily: "Versículo, devocional e oração todos os dias na sua caixa de entrada.",
  plans: "Avisamos quando novos planos de leitura forem publicados.",
  "prayer-week": "Sete dias de oração guiada pelo pastor Carlos.",
  all: "Mensagens pastorais diárias direto da equipe do pastor Carlos.",
};

export default function NewsletterCTA({
  variant = "inline",
  interest = "daily",
  title,
  subtitle,
  testidPrefix = "newsletter-cta",
}) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [hint, setHint] = useState("");

  const finalTitle = title || DEFAULT_TITLES[interest] || DEFAULT_TITLES.daily;
  const finalSubtitle = subtitle || DEFAULT_SUBTITLES[interest] || DEFAULT_SUBTITLES.daily;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!email.trim() || loading) return;
    setLoading(true);
    setHint("");
    try {
      const { data } = await api.post("/integrations/newsletter/subscribe", {
        email: email.trim(),
        interest,
        source: "app",
      });
      if (data?.available && data?.subscribed) {
        setDone(true);
        toast.success(data.message || "Inscrição confirmada.");
      } else {
        // Endpoint da landing ainda não publicado.
        setHint(data?.message || "Inscrição por e-mail em breve.");
        toast(data?.message || "Inscrição por e-mail em breve.");
      }
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        (Array.isArray(err?.response?.data?.detail) && err.response.data.detail[0]?.msg) ||
        "Não foi possível concluir agora. Tente novamente em instantes.";
      setHint(typeof detail === "string" ? detail : "Não foi possível concluir agora.");
      toast.error(typeof detail === "string" ? detail : "Não foi possível concluir agora.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid={`${testidPrefix}-success`}
        className="rounded-2xl border border-emerald-300/60 bg-emerald-50/70 px-5 py-4 flex items-start gap-3"
      >
        <Check className="w-5 h-5 text-emerald-700 mt-0.5 shrink-0" />
        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-[0.3em] text-emerald-800/80">
            Confirmado
          </p>
          <p className="mt-1 text-navy text-sm sm:text-[15px] leading-relaxed">
            Pronto. As próximas mensagens chegam direto no seu e-mail.
          </p>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid={testidPrefix}
      className="rounded-2xl border border-navy/12 bg-white px-5 py-4 sm:p-5"
    >
      <div className="flex items-start gap-3">
        <span className="mt-1 w-8 h-8 rounded-full bg-gold/12 flex items-center justify-center shrink-0">
          <Mail className="w-4 h-4 text-gold" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] uppercase tracking-[0.3em] text-gold">Por e-mail</p>
          <p
            className="mt-1 font-display text-lg text-navy leading-snug"
            data-testid={`${testidPrefix}-title`}
          >
            {finalTitle}
          </p>
          <p className="mt-1 text-sm text-navy/65 leading-relaxed">{finalSubtitle}</p>

          <div className="mt-3 flex flex-col sm:flex-row gap-2">
            <input
              type="email"
              inputMode="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="seu@email.com"
              data-testid={`${testidPrefix}-email`}
              className="flex-1 rounded-full border border-navy/15 bg-white px-4 py-2.5 text-sm text-navy placeholder:text-navy/40 focus:outline-none focus:border-gold focus:ring-2 focus:ring-gold/20"
            />
            <button
              type="submit"
              disabled={loading || !email.trim()}
              data-testid={`${testidPrefix}-submit`}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-navy text-white px-5 py-2.5 text-[11px] uppercase tracking-[0.25em] disabled:opacity-50 hover:bg-navy/90 transition-colors"
            >
              {loading ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Send className="w-3.5 h-3.5" />
              )}
              Inscrever
            </button>
          </div>

          {hint && (
            <p
              data-testid={`${testidPrefix}-hint`}
              className="mt-2 text-xs text-navy/55"
            >
              {hint}
            </p>
          )}
        </div>
      </div>
    </form>
  );
}
